import { RefObject } from "react";
import { Box, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  colorBorder,
  colorSurface,
  colorTextSecondary,
  radiusCard,
  shadowCard,
} from "src/design/tokens";
import { Deal } from "src/business/deal";
import {
  BookingSegment,
  DayView,
  FULL_DAY_VIEW,
  OpenWindow,
} from "../dayGridHelpers";
import { DropPreview, TableColumn } from "./TableColumn";
import { TimeAxis } from "./TimeAxis";

const AXIS_WIDTH = 42;

interface DayGridProps {
  tables: number[];
  segments: BookingSegment[];
  openWindows: OpenWindow[];
  /** Deal or event covering each row of the day. */
  dealRows: (Deal | undefined)[];
  isPlacing: boolean;
  canCreate: boolean;
  draggingBookingId: string | null;
  dropPreview: DropPreview | null;
  /** Rows to render; defaults to the whole day. */
  view?: DayView;
  /** Must wrap exactly the cell area — the drag maths reads its box. */
  columnsRef: RefObject<HTMLDivElement>;
  onSlotClick: (table: number, row: number) => void;
  onBlockPointerDown: (
    event: React.PointerEvent,
    segment: BookingSegment,
  ) => void;
  onBlockClick: (segment: BookingSegment) => void;
  sx?: SxProps;
}

/**
 * The whole-day board: tables across the top, 00:00 → 23:30 down the side. It
 * fills the height it is given and never scrolls — rows share the space equally,
 * so the entire day is always on screen.
 */
export const DayGrid = ({
  tables,
  segments,
  openWindows,
  dealRows,
  isPlacing,
  canCreate,
  draggingBookingId,
  dropPreview,
  view = FULL_DAY_VIEW,
  columnsRef,
  onSlotClick,
  onBlockPointerDown,
  onBlockClick,
  sx,
}: DayGridProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        backgroundColor: colorSurface,
        border: `1px solid ${colorBorder}`,
        borderRadius: radiusCard,
        boxShadow: shadowCard,
        overflow: "hidden",
        ...sx,
      }}
    >
      {/* Table headers, aligned with the columns below by the same axis inset. */}
      <Box
        sx={{
          display: "flex",
          flexShrink: 0,
          borderBottom: `1px solid ${colorBorder}`,
        }}
      >
        <Box sx={{ width: AXIS_WIDTH, flexShrink: 0 }} />
        {tables.map((table, index) => (
          <Box
            key={`head-${table}`}
            sx={{
              flex: 1,
              minWidth: 0,
              py: 0.75,
              textAlign: "center",
              borderRight:
                index === tables.length - 1 ? "none" : `1px solid ${colorBorder}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                color: colorTextSecondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t("tableSchedule.tableLabel", { number: table })}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <TimeAxis
          sx={{ width: AXIS_WIDTH }}
          view={view}
          highlightedRows={
            dropPreview
              ? {
                  startRow: dropPreview.startRow,
                  endRow: dropPreview.startRow + dropPreview.rows,
                }
              : null
          }
        />
        <Box ref={columnsRef} sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          {tables.map((table, index) => (
            <TableColumn
              key={`col-${table}`}
              table={table}
              segments={segments.filter((segment) => segment.table === table)}
              openWindows={openWindows}
              dealRows={dealRows}
              view={view}
              isPlacing={isPlacing}
              canCreate={canCreate}
              draggingBookingId={draggingBookingId}
              dropPreview={dropPreview}
              isLastColumn={index === tables.length - 1}
              onSlotClick={onSlotClick}
              onBlockPointerDown={onBlockPointerDown}
              onBlockClick={onBlockClick}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
